import { useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { FileDropzone } from '@/components/FileDropzone/FileDropzone';
import { TextField } from '@/components/TextField/TextField';
import type { Branding } from '@/graphql/brokerage-settings';
import type { UploadProgress } from '@/lib/upload';

import styles from './BrandingForm.module.css';

export const LOGO_TYPES = 'image/png,image/jpeg,image/gif,image/webp';

export interface BrandingDraft {
  companyName: string;
  website: string;
  /** A new logo to upload, `remove` to clear it, or null to keep the current one. */
  logo: File | 'remove' | null;
}

export interface BrandingFormProps {
  branding: Branding;
  canEdit: boolean;
  busy?: boolean;
  progress?: UploadProgress | null;
  error?: string | null;
  onSubmit: (draft: BrandingDraft) => void;
}

/** The name, website and logo a brokerage's emails and the main app show. */
export function BrandingForm({
  branding,
  canEdit,
  busy = false,
  progress = null,
  error = null,
  onSubmit,
}: BrandingFormProps) {
  const [companyName, setCompanyName] = useState(branding.companyName ?? '');
  const [website, setWebsite] = useState(branding.website ?? '');
  const [logo, setLogo] = useState<File | 'remove' | null>(null);

  const unchanged =
    companyName === (branding.companyName ?? '') &&
    website === (branding.website ?? '') &&
    logo === null;
  const showCurrent = branding.logo?.previewUrl && logo === null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({ companyName: companyName.trim(), website: website.trim(), logo });
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.fields}>
        <TextField
          disabled={!canEdit || busy}
          hint="As it appears on emails and in the main app."
          label="Company name"
          onChange={(event) => setCompanyName(event.target.value)}
          value={companyName}
        />
        <TextField
          disabled={!canEdit || busy}
          inputMode="url"
          label="Website"
          onChange={(event) => setWebsite(event.target.value)}
          placeholder="https://"
          value={website}
        />
      </div>

      <div className={styles.logo}>
        <span className={styles.label}>Logo</span>
        {showCurrent ? (
          <div className={styles.current}>
            <img alt="Current logo" className={styles.preview} src={branding.logo!.previewUrl!} />
            {canEdit ? (
              <Button
                disabled={busy}
                onClick={() => setLogo('remove')}
                size="sm"
                type="button"
                variant="quiet"
              >
                Remove logo
              </Button>
            ) : null}
          </div>
        ) : logo === 'remove' ? (
          <p className={styles.note}>
            The logo will be removed.{' '}
            <Button onClick={() => setLogo(null)} size="sm" type="button" variant="ghost">
              Keep it
            </Button>
          </p>
        ) : !canEdit ? (
          <p className={styles.note}>No logo.</p>
        ) : null}
        {canEdit && logo !== 'remove' ? (
          <FileDropzone
            accept={LOGO_TYPES}
            disabled={busy}
            file={logo instanceof File ? logo : null}
            hint="PNG, JPEG, GIF or WebP, up to 5 MB."
            label={branding.logo ? 'Replace the logo' : 'Upload a logo'}
            onChange={(file) => setLogo(file)}
            progress={progress}
          />
        ) : null}
      </div>

      {error ? (
        <Callout tone="danger" title="That didn't save">
          {error}
        </Callout>
      ) : null}

      {canEdit ? (
        <div className={styles.actions}>
          <Button disabled={unchanged} loading={busy} type="submit">
            Save branding
          </Button>
        </div>
      ) : null}
    </form>
  );
}
